import { API_ENDPOINTS, apiService } from "../api";
import {
  createMockStoryRefreshSchedule,
  getMockStoryRefreshSchedule,
  replaceMockStoryRefreshSchedule,
} from "./storyScheduleApi.mock";
import { mapStoryRefreshScheduleResponse } from "./storyScheduleMappers";
import type {
  StoryRefreshScheduleResponseApi,
  StoryRefreshScheduleState,
  StoryRefreshScheduleWriteRequestApi,
} from "./storySchedule.types";

/**
 * This endpoint stays mock-first until its backend implementation is ready.
 * Accept both the requested boolean-style flag and the repository's usual
 * `live` spelling so promotion is an environment-only change.
 */
const isLiveStoryRefreshScheduleApi = (): boolean => {
  const value = process.env.REACT_APP_STORY_REFRESH_SCHEDULE_API?.toLowerCase();
  return value === "mock";
};

const endpoint = (workspaceId: string, storyId: string): string =>
  API_ENDPOINTS.stories.refreshSchedule(workspaceId, storyId);

/** GET the existing schedule. `schedule: null` is a successful empty state. */
export const getStoryRefreshSchedule = async (
  workspaceId: string,
  storyId: string,
): Promise<StoryRefreshScheduleState> => {
  const response = !isLiveStoryRefreshScheduleApi()
    ? await apiService.get<StoryRefreshScheduleResponseApi>(
        endpoint(workspaceId, storyId),
      )
    : await getMockStoryRefreshSchedule(workspaceId, storyId);

  return mapStoryRefreshScheduleResponse(response);
};

/** POST a new schedule after GET returned `schedule: null`. */
export const createStoryRefreshSchedule = async (
  workspaceId: string,
  storyId: string,
  request: StoryRefreshScheduleWriteRequestApi,
): Promise<StoryRefreshScheduleState> => {
  const response = !isLiveStoryRefreshScheduleApi()
    ? await apiService.post<StoryRefreshScheduleResponseApi>(
        endpoint(workspaceId, storyId),
        request,
      )
    : await createMockStoryRefreshSchedule(workspaceId, storyId, request);

  return mapStoryRefreshScheduleResponse(response);
};

/** PUT a full replacement after GET returned an existing schedule. */
export const replaceStoryRefreshSchedule = async (
  workspaceId: string,
  storyId: string,
  request: StoryRefreshScheduleWriteRequestApi,
): Promise<StoryRefreshScheduleState> => {
  const response = !isLiveStoryRefreshScheduleApi()
    ? await apiService.put<StoryRefreshScheduleResponseApi>(
        endpoint(workspaceId, storyId),
        request,
      )
    : await replaceMockStoryRefreshSchedule(workspaceId, storyId, request);

  return mapStoryRefreshScheduleResponse(response);
};
